import { useQueryClient, useMutation } from '@tanstack/react-query'
import { timeCardKeys, entryKeys } from '@lib/keys'
import {
  createCorrectedTimeCard,
  deleteCorrectedTimeCard,
  updateCorrectedTimeCard,
  saveCorrectedTimeCard,
} from '@lib/api'
import { DateTime } from 'luxon'
import { useUserId } from './useUserId'
import { UpdateTimeCardMutationOptions } from './useTimeCardManagement'

interface WeekRangeOptions {
  start: DateTime
  end: DateTime
}

interface CreateCorrectionOptions extends WeekRangeOptions {
  date: DateTime
}

export const useCreateCorrection = ({
  date,
  start,
  end,
}: CreateCorrectionOptions) => {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation(() => createCorrectedTimeCard({ userId, date }), {
    onSuccess: () => {
      void queryClient.invalidateQueries(
        timeCardKeys.search(userId, start, end),
      )

      void queryClient.invalidateQueries(entryKeys.search(userId, start, end))
    },
  })
}

export const useDeleteCorrection = ({ start, end }: WeekRangeOptions) => {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation(
    (timeCardId: number) => deleteCorrectedTimeCard({ userId, timeCardId }),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(
          timeCardKeys.search(userId, start, end),
        )

        void queryClient.invalidateQueries(entryKeys.search(userId, start, end))
      },
    },
  )
}

export const useUpdateCorrection = ({ start, end }: WeekRangeOptions) => {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation(
    // This shares an interface with useUpdateTimeCard to simplify for consumers
    (options: UpdateTimeCardMutationOptions) =>
      updateCorrectedTimeCard({ ...options, userId }),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(
          timeCardKeys.search(userId, start, end),
        )
      },
    },
  )
}

export const useSaveCorrection = ({ start, end }: WeekRangeOptions) => {
  const queryClient = useQueryClient()
  const userId = useUserId()

  return useMutation(
    (timeCardId: number) => saveCorrectedTimeCard({ userId, timeCardId }),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries(
          timeCardKeys.search(userId, start, end),
        )
      },
    },
  )
}
